import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  IconButton,
  Badge,
  Grid,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Image as ImageIcon,
  DragIndicator,
  Star
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Image Item Component
const SortableImageItem = ({ id, index, file, onRemove, disabled, slotLabels, formatFileSize, deletingStatus }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  
  const isDeleting = deletingStatus === 'deleting';

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  useEffect(() => {
    if (file instanceof File) {
      const objectUrl = URL.createObjectURL(file);
      setImageSrc(objectUrl);
      
      // Cleanup object URL on unmount
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else if (typeof file === 'string') {
      setImageSrc(file);
    } else if (file && file.url) {
      setImageSrc(file.url);
    } else if (file && file.preview) {
      setImageSrc(file.preview);
    }
  }, [file]);

  return (
    <Grid size={{ xs: 6, sm: 4, md: 3 }} ref={setNodeRef} style={style} sx={{ minWidth: '120px', maxWidth: '120px' }}>
      <Box
        sx={{
          width: '100%',
          minWidth: '120px',
          maxWidth: '120px'
        }}
      >
        <Paper
          elevation={isDragging ? 8 : 2}
          sx={{
            position: 'relative',
            paddingTop: '100%', // Maintain 1:1 aspect ratio
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            border: index === 0 ? '3px solid #4CAF50' : '2px solid #e0e0e0',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: 6,
              '& .drag-handle': {
                opacity: 1
              },
              '& .delete-button': {
                opacity: 1
              }
            }
          }}
        >
        {/* Main Image Badge */}
        {index === 0 && (
          <Chip
            icon={<Star sx={{ fontSize: 16 }} />}
            label={slotLabels.main}
            color="success"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 2,
              fontWeight: 600,
              fontSize: '0.7rem'
            }}
          />
        )}

        {/* Drag Handle */}
        <Box
          {...listeners}
          {...attributes}
          className="drag-handle"
          sx={{
            position: 'absolute',
            top: 8,
            ...(index === 0 ? { right: 8, top: 40 } : { left: 8 }),
            zIndex: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: 1,
            cursor: 'grab',
            opacity: 0,
            transition: 'opacity 0.2s ease',
            padding: '4px',
            '&:active': {
              cursor: 'grabbing'
            }
          }}
        >
          <DragIndicator sx={{ color: 'white', fontSize: 20 }} />
        </Box>

        {/* Delete Button */}
        {!disabled && !isDeleting && (
          <IconButton
            className="delete-button"
            size="small"
            onClick={() => onRemove(index)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              backgroundColor: 'error.main',
              color: 'white',
              opacity: 0,
              transition: 'opacity 0.2s ease',
              '&:hover': {
                backgroundColor: 'error.dark',
              },
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        )}

        {/* Deleting Overlay */}
        {isDeleting && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: 'white', mb: 1 }} />
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
              Deleting...
            </Typography>
          </Box>
        )}

        {/* Image Preview */}
        {imageSrc && !imageError ? (
          <Box
            component="img"
            src={imageSrc}
            alt={`Product image ${index + 1}`}
            onError={(e) => {
              console.error(`Failed to load image ${index}:`, e);
              setImageError(true);
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5'
            }}
          >
            <ImageIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
              {file && file.name ? file.name : 'Image'}
            </Typography>
          </Box>
        )}

        {/* Image Index */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            fontSize: '0.75rem',
            fontWeight: 600
          }}
        >
          {index + 1}
        </Box>

        {/* File Size */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            fontSize: '0.65rem'
          }}
        >
          {file instanceof File ? formatFileSize(file.size) : 'N/A'}
        </Box>
      </Paper>
      </Box>
    </Grid>
  );
};

const ImageUploadDragDrop = ({
  value = [],
  onChange,
  minFiles = 4,
  maxFiles = 20,
  maxFileSize = 2 * 1024 * 1024, // 2MB default
  acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  label = 'Product Images',
  helperText = 'Drag and drop images here or click to browse',
  error = false,
  errorMessage = '',
  disabled = false,
  imageGuidelines = {},
  slotLabels = { main: 'Main Image', additional: 'Image' },
  sx = {}
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [deletingStatuses, setDeletingStatuses] = useState({});
  const fileInputRef = useRef(null);

  const currentImages = Array.isArray(value) ? value.filter(img => img !== null && img !== undefined) : [];

  // Setup sensors for dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const validateFile = (file) => {
    // Check file size
    const maxSizeMB = maxFileSize / (1024 * 1024);
    if (file.size > maxFileSize) {
      return `File "${file.name}" exceeds ${maxSizeMB.toFixed(1)}MB limit`;
    }

    // Check file type
    if (!acceptedTypes.includes(file.type)) {
      return `File "${file.name}" type not supported. Accepted: ${acceptedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')}`;
    }

    return null;
  };

  const handleFiles = (files) => {
    setValidationError('');
    const fileList = Array.from(files);
    
    // Check if adding these files would exceed maxFiles
    if (currentImages.length + fileList.length > maxFiles) {
      setValidationError(`Cannot upload more than ${maxFiles} images. Currently have ${currentImages.length} images.`);
      return;
    }

    // Validate each file
    const errors = [];
    const validFiles = [];
    
    fileList.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setValidationError(errors.join('; '));
      return;
    }

    if (validFiles.length > 0) {
      const newImages = [...currentImages, ...validFiles];
      onChange(newImages);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [disabled, currentImages, maxFiles]);

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleRemove = (index) => {
    const newImages = currentImages.filter((_, i) => i !== index);
    onChange(newImages);
    setValidationError('');
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = currentImages.findIndex((_, idx) => `image-${idx}` === active.id);
      const newIndex = currentImages.findIndex((_, idx) => `image-${idx}` === over.id);

      const newImages = arrayMove(currentImages, oldIndex, newIndex);
      onChange(newImages);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {label}
      </Typography>

      {/* Image Guidelines */}
      {imageGuidelines.description && (
        <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
          <strong>Guidelines:</strong> {imageGuidelines.description}
          {imageGuidelines.minDimensions && imageGuidelines.maxDimensions && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Dimensions: {imageGuidelines.minDimensions} to {imageGuidelines.maxDimensions} pixels
            </Typography>
          )}
        </Alert>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        multiple
        onChange={handleFileInput}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {/* Drag and Drop Zone */}
      {currentImages.length < maxFiles && (
        <Paper
          elevation={dragActive ? 8 : 2}
          sx={{
            p: 4,
            mb: 3,
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: dragActive ? '3px dashed #4CAF50' : '3px dashed #e0e0e0',
            backgroundColor: dragActive ? '#E8F5E8' : '#fafafa',
            transition: 'all 0.3s ease',
            opacity: disabled ? 0.6 : 1,
            '&:hover': {
              backgroundColor: disabled ? '#fafafa' : '#E8F5E8',
              borderColor: disabled ? '#e0e0e0' : '#4CAF50',
              transform: disabled ? 'none' : 'translateY(-2px)',
              boxShadow: disabled ? 2 : 4
            }
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <CloudUpload sx={{ fontSize: 56, color: dragActive ? '#4CAF50' : '#9e9e9e', mb: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            {helperText}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            or click to select files
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Maximum {maxFiles} images • Maximum {(maxFileSize / (1024 * 1024)).toFixed(1)}MB per image
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Supported formats: {acceptedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')}
          </Typography>
        </Paper>
      )}

      {/* Image Count Status */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {currentImages.length} of {maxFiles} images uploaded
        </Typography>
        {currentImages.length < minFiles && (
          <Chip 
            label={`Minimum ${minFiles} images required`} 
            color="warning" 
            size="small"
            sx={{ fontWeight: 500 }}
          />
        )}
        {currentImages.length >= minFiles && (
          <Chip 
            label="Minimum requirement met" 
            color="success" 
            size="small"
            sx={{ fontWeight: 500 }}
          />
        )}
      </Box>

      {/* Image Preview Grid with Drag and Drop Reordering */}
      {currentImages.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            💡 Tip: Drag images to reorder. The first image will be used as the main product image.
          </Typography>
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={currentImages.map((_, idx) => `image-${idx}`)}
              strategy={rectSortingStrategy}
            >
              <Grid container spacing={2}>
                {currentImages.map((file, index) => (
                  <SortableImageItem
                    key={`image-${index}`}
                    id={`image-${index}`}
                    index={index}
                    file={file}
                    onRemove={handleRemove}
                    disabled={disabled}
                    slotLabels={slotLabels}
                    formatFileSize={formatFileSize}
                    deletingStatus={deletingStatuses[index]}
                  />
                ))}
              </Grid>
            </SortableContext>
          </DndContext>
        </Box>
      )}

      {/* Validation Error */}
      {validationError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {validationError}
        </Alert>
      )}

      {/* Field Error */}
      {error && errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}
    </Box>
  );
};

export default ImageUploadDragDrop;

